/* eslint-disable no-console */

/**
 * Console instrumentation.
 *
 * A scanner that says only "ambiguous" is impossible to reason about from a phone, so
 * every stage reports what it saw and what it cost. Three levels, because the useful
 * granularity differs by two orders of magnitude between "the embedder loaded" and "frame
 * 4,102 found one quad":
 *
 * - **on** (default) — boot, and one fully itemised block per recognition.
 * - **trace** (`?trace`) — adds a line for every frame. A firehose at 30 fps, and the only
 *   way to see why the gate never settles.
 * - **quiet** (`?quiet`) — nothing.
 *
 * Timings come from `performance.now()` and are wall-clock per stage, so they include time
 * the stage spent yielding. That is the number that matters for "why is this slow".
 */

const search = typeof location === "undefined" ? "" : location.search;

export const VERBOSE = !/[?&]quiet\b/.test(search);

/**
 * Live, not constant: per-frame logging is a switch in the settings panel as well as a
 * URL flag, and a `let` export is a live binding — every importer sees the new value at
 * the moment {@link setTrace} runs, without a reload and without a listener each.
 *
 * Seeded here rather than read from `settings` to keep this module dependency-free: it is
 * imported by everything, including the settings store's own callers.
 */
export let TRACE = /[?&]trace\b/.test(search);

export function setTrace(on: boolean): void {
  TRACE = on;
}

const COLOURS: Record<string, string> = {
  boot: "#4c8dff",
  model: "#a371f7",
  frame: "#8b97a6",
  detect: "#3fb950",
  read: "#d29922",
  verdict: "#f0883e",
};

const started = typeof performance === "undefined" ? 0 : performance.now();

/** Milliseconds since the module loaded, so lines can be correlated with the UI. */
const stamp = () => `${((performance.now() - started) / 1000).toFixed(2)}s`;

/* ---------------------------------------------------------------- black box */

/**
 * The last {@link RING_SIZE} lines, mirrored to `localStorage`.
 *
 * When iOS Safari runs out of memory it does not throw — the tab process is killed and
 * the console goes with it, so the most interesting lines are exactly the ones that never
 * reach a debugger. Checkpointing them means the next load can show what the previous
 * one was doing when it died, on the phone, with no cable and no Mac.
 *
 * Writes are throttled: `localStorage` is synchronous and on the main thread, which is
 * not somewhere to spend time once a frame.
 */
const RING_SIZE = 300;
const LINE_MAX = 400;
const STORE_KEY = "webscan2:log";
const PREVIOUS_KEY = "webscan2:log:previous";
// Keep routine telemetry in memory and checkpoint infrequently: serialising the whole
// ring is synchronous. Faults and backgrounding flush immediately, before iOS can suspend
// the tab. An abrupt process kill may lose the final five seconds of ordinary detail.
const FLUSH_MS = 5_000;
const TRACE_SAMPLE_MS = 1_000;

const ring: string[] = [];
const traceSamples = new Map<string, number>();
let flushAt = 0;
let flushTimer: ReturnType<typeof setTimeout> | null = null;

/** Lines from the run before this one — populated at load if the last run left any. */
export let previousRun: string[] = [];

function store(key: string, lines: string[]) {
  try {
    localStorage.setItem(key, JSON.stringify(lines));
  } catch {
    /* private mode, or quota — the black box is a convenience, never a dependency */
  }
}

function flush(force = false) {
  const now = Date.now();
  if (!force && now - flushAt < FLUSH_MS) {
    flushTimer ??= setTimeout(() => flush(true), FLUSH_MS - (now - flushAt));
    return;
  }
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  flushAt = now;
  store(STORE_KEY, ring);
}

function record(level: string, scope: string, message: string, args: unknown[]) {
  let line = `${stamp()} [${level}] ${scope} ${message}`;
  for (const arg of args) {
    if (arg === undefined) continue;
    let rendered: string;
    try {
      rendered = arg instanceof Error ? `${arg.name}: ${arg.message}` : JSON.stringify(arg);
    } catch {
      rendered = String(arg);
    }
    line += ` ${rendered}`;
  }
  ring.push(line.length > LINE_MAX ? `${line.slice(0, LINE_MAX)}…` : line);
  if (ring.length > RING_SIZE) ring.splice(0, ring.length - RING_SIZE);
  sink?.(line);
  flush(level === "warn" || level === "error");
}

/** Optional live mirror, so a phone can watch without a debugger. */
let sink: ((line: string) => void) | null = null;
export function onLine(handler: (line: string) => void): void {
  sink = handler;
}

/** Everything captured this run, newest last. */
export const snapshot = (): string[] => ring.slice();

export function clearBlackBox(): void {
  previousRun = [];
  try {
    localStorage.removeItem(PREVIOUS_KEY);
    localStorage.removeItem(STORE_KEY);
  } catch {
    /* nothing to clear */
  }
}

/**
 * Rotate the last run's lines aside and start a fresh buffer.
 *
 * A clean exit clears the live key on `pagehide`, so anything still there at load means
 * the previous run ended without one — a crash, a reload, or a tab the OS reclaimed.
 */
function openBlackBox() {
  if (typeof localStorage === "undefined") return;
  let pageHidden = false;
  try {
    const carried = localStorage.getItem(STORE_KEY);
    if (carried) {
      previousRun = JSON.parse(carried) as string[];
      store(PREVIOUS_KEY, previousRun);
    }
    localStorage.removeItem(STORE_KEY);
  } catch {
    previousRun = [];
  }
  addEventListener("pagehide", () => {
    // A deliberate navigation is not a crash; drop the live buffer so the next load does
    // not report one. Anything that skips this handler leaves its trail behind.
    pageHidden = true;
    flush(true);
    try {
      localStorage.removeItem(STORE_KEY);
    } catch {
      /* nothing to remove */
    }
  });
  addEventListener("pageshow", () => {
    pageHidden = false;
  });
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => {
      // Some browsers deliver this after pagehide. Do not recreate the clean-exit key.
      if (!pageHidden && document.visibilityState === "hidden") flush(true);
    });
  }
  // Memory pressure and uncaught faults are the two things most likely to precede a kill.
  addEventListener("error", (event) =>
    record("error", "window", `uncaught: ${event.message} @ ${event.filename}:${event.lineno}`, []),
  );
  addEventListener("unhandledrejection", (event) =>
    record("error", "window", `unhandled rejection: ${String(event.reason)}`, []),
  );
}

if (VERBOSE) openBlackBox();

function emit(method: "log" | "warn" | "error", scope: string, message: string, args: unknown[]) {
  const colour = COLOURS[scope] ?? "#8b97a6";
  console[method](
    `%c${stamp()}%c ${scope}%c ${message}`,
    "color:#586069",
    `color:${colour};font-weight:600`,
    "color:inherit",
    ...args,
  );
  record(method, scope, message, args);
}

export function log(scope: string, message: string, ...args: unknown[]): void {
  if (VERBOSE) emit("log", scope, message, args);
}

/**
 * Per-frame detail. Full output is opt-in; normal runs retain at most one sample per
 * scope per second in the black box. Use a message callback on hot paths so discarded
 * samples do not format strings or calculate diagnostic-only values.
 */
export function trace(scope: string, message: string | (() => string), ...args: unknown[]): void {
  if (!TRACE && !VERBOSE) return;
  if (!TRACE) {
    const now = performance.now();
    const previous = traceSamples.get(scope);
    if (previous !== undefined && now - previous < TRACE_SAMPLE_MS) return;
    traceSamples.set(scope, now);
  }
  const text = typeof message === "function" ? message() : message;
  if (TRACE) emit("log", scope, text, args);
  else record("trace", scope, text, args);
}

export function warn(scope: string, message: string, ...args: unknown[]): void {
  emit("warn", scope, message, args);
}

/** A collapsed group, so one recognition is one foldable block rather than 20 lines. */
export function group(scope: string, message: string): void {
  if (!VERBOSE) return;
  const colour = COLOURS[scope] ?? "#8b97a6";
  console.groupCollapsed(
    `%c${stamp()}%c ${scope}%c ${message}`,
    "color:#586069",
    `color:${colour};font-weight:600`,
    "color:inherit",
  );
}

export function groupEnd(): void {
  if (VERBOSE) console.groupEnd();
}

export function table(rows: unknown[]): void {
  if (VERBOSE && rows.length) console.table(rows);
}

/** Start a stopwatch; the returned function stops it and yields elapsed milliseconds. */
export function clock(): () => number {
  const from = performance.now();
  return () => performance.now() - from;
}

export const ms = (value: number) => `${value.toFixed(1)} ms`;
export const mb = (bytes: number) => `${(bytes / 1e6).toFixed(1)} MB`;
